import React, { useState } from 'react';
import {
  FiArrowRight as ArrowRight,
  FiArrowUpRight as ArrowUpRight,
  FiArrowDownRight as ArrowDownRight,
  FiMinus as Minus,
  FiGitBranch as GitCompare,
  FiAlertCircle as AlertCircle,
  FiInfo as Info,
} from 'react-icons/fi';
import { compareVersions } from '../../../../services/aiOperationsApi';

type MetricFormat = 'currency' | 'percent' | 'ms' | 'tokens' | 'number';

interface MetricCardProps {
  label: string;
  valueA: number;
  valueB: number;
  format?: MetricFormat;
  isHigherBetter?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  valueA,
  valueB,
  format = 'number',
  isHigherBetter = false,
}) => {
  const diff = valueB - valueA;
  const percentDiff = valueA === 0 ? 0 : ((valueB - valueA) / valueA) * 100;

  const formatValue = (val: number): string => {
    if (format === 'currency') return `$${val.toFixed(4)}`;
    if (format === 'percent') return `${val.toFixed(1)}%`;
    if (format === 'ms') return `${val.toFixed(0)}ms`;
    if (format === 'tokens') return val.toLocaleString();
    return val.toFixed(2);
  };

  const isImprovement = isHigherBetter ? diff > 0 : diff < 0;
  const isRegression = isHigherBetter ? diff < 0 : diff > 0;

  let diffColorClass: string;
  if (isImprovement) diffColorClass = 'text-green-600 dark:text-green-400';
  else if (isRegression) diffColorClass = 'text-red-600 dark:text-red-400';
  else diffColorClass = 'text-gray-500';

  let DiffIcon: React.ComponentType<{ className?: string }>;
  if (isImprovement) DiffIcon = ArrowDownRight;
  else if (isRegression) DiffIcon = ArrowUpRight;
  else DiffIcon = Minus;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
      <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">{label}</div>
      <div className="flex items-end justify-between">
        <div className="flex items-baseline space-x-3">
          <span className="text-lg font-mono text-gray-500 dark:text-gray-400">{formatValue(valueA)}</span>
          <ArrowRight className="w-4 h-4 text-gray-400" />
          <span className="text-xl font-mono font-semibold text-gray-900 dark:text-white">{formatValue(valueB)}</span>
        </div>
        {diff !== 0 && (
          <div className={`flex items-center text-sm ${diffColorClass}`}>
            <DiffIcon className="w-4 h-4 mr-1" />
            <span className="font-medium">{Math.abs(percentDiff).toFixed(1)}%</span>
          </div>
        )}
      </div>
    </div>
  );
};

interface VersionStats {
  version_number: number;
  total_runs: number;
  success_rate: number;
  avg_cost: number;
  avg_latency_ms: number;
  p50_latency_ms: number;
  p95_latency_ms: number;
  total_cost: number;
}

interface ComparisonResult {
  version_a: VersionStats;
  version_b: VersionStats;
  recommendation: string;
  is_statistically_significant: boolean;
  cost_diff_percent: number;
  latency_diff_percent: number;
  success_rate_diff: number;
  token_diff_percent: number;
}

interface VersionComparisonSectionProps {
  promptId: string;
  versions?: number[];
  collapsed?: boolean;
}

const VersionComparisonSection: React.FC<VersionComparisonSectionProps> = ({
  promptId,
  versions = [],
  collapsed = false,
}) => {
  const [versionA, setVersionA] = useState('');
  const [versionB, setVersionB] = useState('');
  const [dateRange, setDateRange] = useState('30');
  const [comparison, setComparison] = useState<ComparisonResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(!collapsed);

  const handleCompare = async () => {
    if (!promptId || !versionA || !versionB) return;

    setLoading(true);
    setError(null);

    try {
      const now = new Date();
      const dateFrom = new Date(now.getTime() - Number.parseInt(dateRange) * 24 * 60 * 60 * 1000);

      const data = await compareVersions({
        prompt_id: promptId,
        version_a: Number.parseInt(versionA),
        version_b: Number.parseInt(versionB),
        date_from: dateFrom.toISOString(),
        date_to: now.toISOString(),
      });

      setComparison(data);
    } catch (err: unknown) {
      console.error('Error comparing versions:', err);
      setError(err instanceof Error ? err.message : 'Failed to compare versions');
    } finally {
      setLoading(false);
    }
  };

  if (versions.length < 2) {
    return null;
  }

  const sortedVersions = [...versions].sort((a, b) => b - a);

  const getRecommendationStyle = (): string => {
    if (!comparison) return '';
    if (comparison.recommendation.includes('recommended')) {
      return 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800';
    }
    if (comparison.recommendation.includes('preferred')) {
      return 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800';
    }
    return 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800';
  };

  const getRecommendationIconClass = (): string => {
    if (!comparison) return '';
    if (comparison.recommendation.includes('recommended')) return 'text-green-600 dark:text-green-400';
    if (comparison.recommendation.includes('preferred')) return 'text-amber-600 dark:text-amber-400';
    return 'text-blue-600 dark:text-blue-400';
  };

  const getRecommendationTextClass = (): string => {
    if (!comparison) return '';
    if (comparison.recommendation.includes('recommended')) return 'text-green-800 dark:text-green-300';
    if (comparison.recommendation.includes('preferred')) return 'text-amber-800 dark:text-amber-300';
    return 'text-blue-800 dark:text-blue-300';
  };

  const getCostDiffClass = (): string => {
    if (!comparison) return 'text-gray-600';
    if (comparison.cost_diff_percent < 0) return 'text-green-600';
    if (comparison.cost_diff_percent > 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getLatencyDiffClass = (): string => {
    if (!comparison) return 'text-gray-600';
    if (comparison.latency_diff_percent < 0) return 'text-green-600';
    if (comparison.latency_diff_percent > 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getSuccessDiffClass = (): string => {
    if (!comparison) return 'text-gray-600';
    if (comparison.success_rate_diff > 0) return 'text-green-600';
    if (comparison.success_rate_diff < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getTokenDiffClass = (): string => {
    if (!comparison) return 'text-gray-600';
    if (comparison.token_diff_percent < 0) return 'text-green-600';
    if (comparison.token_diff_percent > 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const content = (
    <>
      {/* Selection Controls */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div>
          <label htmlFor="version-a-baseline" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Version A (Baseline)
          </label>
          <select id="version-a-baseline"
            value={versionA}
            onChange={(e) => {
              setVersionA(e.target.value);
              setComparison(null);
            }}
            className="w-full rounded-lg border-gray-300 dark:border-gray-600 bg-white text-gray-900 dark:bg-gray-700 dark:text-white text-sm"
          >
            <option value="">Select...</option>
            {sortedVersions.map((v) => (
              <option key={v} value={v}>
                v{v}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="version-b-compare" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Version B (Compare)
          </label>
          <select id="version-b-compare"
            value={versionB}
            onChange={(e) => {
              setVersionB(e.target.value);
              setComparison(null);
            }}
            className="w-full rounded-lg border-gray-300 dark:border-gray-600 bg-white text-gray-900 dark:bg-gray-700 dark:text-white text-sm"
          >
            <option value="">Select...</option>
            {sortedVersions
              .filter((v) => v !== Number.parseInt(versionA))
              .map((v) => (
                <option key={v} value={v}>
                  v{v}
                </option>
              ))}
          </select>
        </div>

        <div>
          <label htmlFor="time-range" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Time Range
          </label>
          <select id="time-range"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="w-full rounded-lg border-gray-300 dark:border-gray-600 bg-white text-gray-900 dark:bg-gray-700 dark:text-white text-sm"
          >
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
            <option value="60">Last 60 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>

        <div className="flex items-end">
          <button
            onClick={handleCompare}
            disabled={!versionA || !versionB || loading}
            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Comparing...
              </>
            ) : (
              <>
                <GitCompare className="w-4 h-4 mr-2" /> Compare
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
          <div className="flex items-center text-sm">
            <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mr-2" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {comparison && (
        <>
          {/* Recommendation Banner */}
          <div className={`rounded-lg p-3 mb-4 ${getRecommendationStyle()}`}>
            <div className="flex items-start">
              <Info className={`w-4 h-4 mr-2 mt-0.5 flex-shrink-0 ${getRecommendationIconClass()}`} />
              <div className="text-sm">
                <p className={`font-medium ${getRecommendationTextClass()}`}>
                  {comparison.recommendation}
                </p>
                {!comparison.is_statistically_significant && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex items-center">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Not statistically significant (need more data)
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Version Headers */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 text-center">
              <span className="text-xs text-gray-500 dark:text-gray-400">Baseline</span>
              <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                v{comparison.version_a.version_number}
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {comparison.version_a.total_runs.toLocaleString()} runs
              </p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center border border-blue-200 dark:border-blue-700">
              <span className="text-xs text-blue-600 dark:text-blue-400">Comparing</span>
              <h4 className="text-lg font-bold text-blue-900 dark:text-blue-100">
                v{comparison.version_b.version_number}
              </h4>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                {comparison.version_b.total_runs.toLocaleString()} runs
              </p>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            <MetricCard
              label="Success Rate"
              valueA={comparison.version_a.success_rate}
              valueB={comparison.version_b.success_rate}
              format="percent"
              isHigherBetter
            />
            <MetricCard
              label="Avg Cost"
              valueA={comparison.version_a.avg_cost}
              valueB={comparison.version_b.avg_cost}
              format="currency"
            />
            <MetricCard
              label="Avg Latency"
              valueA={comparison.version_a.avg_latency_ms}
              valueB={comparison.version_b.avg_latency_ms}
              format="ms"
            />
            <MetricCard
              label="P50 Latency"
              valueA={comparison.version_a.p50_latency_ms}
              valueB={comparison.version_b.p50_latency_ms}
              format="ms"
            />
            <MetricCard
              label="P95 Latency"
              valueA={comparison.version_a.p95_latency_ms}
              valueB={comparison.version_b.p95_latency_ms}
              format="ms"
            />
            <MetricCard
              label="Total Cost"
              valueA={comparison.version_a.total_cost}
              valueB={comparison.version_b.total_cost}
              format="currency"
            />
          </div>

          {/* Summary */}
          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-700">
              <div className={`text-lg font-bold ${getCostDiffClass()}`}>
                {comparison.cost_diff_percent > 0 ? '+' : ''}
                {comparison.cost_diff_percent.toFixed(1)}%
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Cost</div>
            </div>
            <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-700">
              <div className={`text-lg font-bold ${getLatencyDiffClass()}`}>
                {comparison.latency_diff_percent > 0 ? '+' : ''}
                {comparison.latency_diff_percent.toFixed(1)}%
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Latency</div>
            </div>
            <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-700">
              <div className={`text-lg font-bold ${getSuccessDiffClass()}`}>
                {comparison.success_rate_diff > 0 ? '+' : ''}
                {comparison.success_rate_diff.toFixed(1)}%
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Success</div>
            </div>
            <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-700">
              <div className={`text-lg font-bold ${getTokenDiffClass()}`}>
                {comparison.token_diff_percent > 0 ? '+' : ''}
                {comparison.token_diff_percent.toFixed(1)}%
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Tokens</div>
            </div>
          </div>
        </>
      )}

      {!comparison && !loading && (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
          Select two versions and click Compare to see performance differences
        </div>
      )}
    </>
  );

  if (collapsed) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-lg"
        >
          <div className="flex items-center">
            <GitCompare className="w-5 h-5 text-blue-600 mr-2" />
            <span className="font-medium text-gray-900 dark:text-white">Version Comparison</span>
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
              ({versions.length} versions available)
            </span>
          </div>
          {isExpanded ? (
            <span className="text-gray-400">▼</span>
          ) : (
            <span className="text-gray-400">▶</span>
          )}
        </button>
        {isExpanded && (
          <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700 pt-4">{content}</div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <div className="flex items-center mb-4">
        <GitCompare className="w-5 h-5 text-blue-600 mr-2" />
        <h3 className="font-semibold text-gray-900 dark:text-white">Version Comparison</h3>
      </div>
      {content}
    </div>
  );
};

export default VersionComparisonSection;
